<template>
  <div>
    <div v-if="criteresRecherche" class="tableWithPerPageSelection">
      <div class="level mgt-20px">
        <div class="level-left">
          <div class="columns">
            <form
              @submit.prevent="recherche()"
              class="column">
              <b-input
                v-model="criteresRecherche.auto"
                :placeholder="$t('general.autocomplete.search')"
                type="search" icon="magnify">
              </b-input>
            </form>
            <div class="column is-narrow">
              <button class="button" @click="setShowFiltre">
                <b-icon icon="filter-variant" size="is-small" type="is-default"></b-icon>
                <span class="is-size-7">
                  {{$t('general.button.filtre')}}
                  <label
                    :class="nombreCritere > 0 ? '' : 'is-invisible'"
                    class="has-text-weight-bold">
                    ({{ nombreCritere }})
                  </label>
                </span>
              </button>
              <span
                @click="reinitialiser()"
                class="info-circle-right is-pulled-right is-cursor-pointer">
                <b-icon icon="close-circle" type="is-danger"></b-icon>
              </span>
            </div>
          </div>
        </div>
        <div class="level-right">
          <div class="column is-narrow">
            <exportExcelButton
              :perm="perms.marche.exporter"
              :query="exportExcelQuery"/>
           </div>
        </div>
      </div>
      <a ref="lienDownload"></a>
      <v-server-table
        url=""
        :columns="columns"
        :options="options"
        @loaded="finLoading"
        @loading="debutLoading"
        class="overflow-x-auto"
        ref="tableListeListeVentesMarche">
        <template slot="appendBody">
          <tr>
            <td colspan="9"></td>
            <td>
              <div class="is-pulled-right is-size-9" ref="totalLigneEch">
                {{ formatDevise(totalColumnsTotalLigneEch) }}
              </div>
            </td>
            <td>
              <div class="is-pulled-right is-size-9" ref="totalPourcentage">
                {{ formatDevise(totalColumnsTotalPourcentage) }}
              </div>
            </td>
            <td class="planningTailleColonnePlanning">
              <div class="disponibiliteSemaine" ref="totauxMonths">
                <div v-for="(month, index) in months" :key="index" class='disponibilite'>
                  <div class="shape has-background-white">
                    <div class="content is-size-9" :ref="month.ref">
                      {{ formatDevise(getTotalEcheancierByMonth(month)) }}
                    </div>
                  </div>
                </div>
              </div>
            </td>
          </tr>
        </template>
        <template slot="planMedia" slot-scope="props">
          <router-link-blank
            v-if="getPlanMedia(props.row)"
            :message="getPlanMedia(props.row).code"
            :to="{
              name: 'c.presse.plan_media',
              params: {id: getPlanMedia(props.row).id}
            }"
            :class="{'text-access-no-permission':
              !checkPermissions(perms.planMedia.lecture)}"
            class="lien ellipsis">
          </router-link-blank>
        </template>
        <template slot="commande" slot-scope="props">
          <router-link-blank
            v-if="getRouteDevisCommande(props.row)"
            v-bind="getRouteDevisCommande(props.row)"
            :class="{'text-access-no-permission': !checkPermissions(perms.commande.lecture)}"
            class="lien ellipsis">
          </router-link-blank>
        </template>
        <template slot="produit" slot-scope="props">
          <router-link-blank
            v-if="getProduit(props.row)"
            :message="getProduit(props.row).reference"
            :to="{
              name: getProduit(props.row).routeDetail,
              params: {id: getProduit(props.row).id}
            }"
            :class="{'text-access-no-permission':
              !checkPermissions(perms.catalogue.lecture)}"
            class="lien ellipsis">
          </router-link-blank>
        </template>
        <template slot="payeur" slot-scope="props">
          <router-link-blank
            v-if="getPayeur(props.row)"
            :message="getPayeur(props.row).nomCommercial"
            :to="{
              name: 'c.bodac.clients/contacts-client',
              params: {id: getPayeur(props.row).tiersId}
            }"
            :class="{'text-access-no-permission':
              !checkPermissions(perms.tiers.lecture)}"
            class="lien ellipsis">
          </router-link-blank>
        </template>
        <template slot="annonceur" slot-scope="props">
          <router-link-blank
            v-if="getAnnonceur(props.row)"
            :message="getAnnonceur(props.row).nomCommercial"
            :to="{
              name: 'c.bodac.clients/contacts-client',
              params: {id: getAnnonceur(props.row).tiersId}
            }"
            :class="{'text-access-no-permission':
              !checkPermissions(perms.tiers.lecture)}"
            class="lien ellipsis">
          </router-link-blank>
        </template>
        <template slot="dateDevisCommande" slot-scope="props">
          <div>
            {{ formatDate(props.row.devisCommande.dateDevisCommande) }}
          </div>
        </template>
        <template slot="dateTransmissionAuClient" slot-scope="props">
          <div>
            {{ formatDate(props.row.devisCommande.dateTransmissionAuClient) }}
          </div>
        </template>
        <template slot="dateTransmissionAuClub" slot-scope="props">
          <div>
            {{ formatDate(props.row.devisCommande.dateTransmissionAuClub) }}
          </div>
        </template>
        <template slot="tauxReversement" slot-scope="props">
          <span v-if="getTauxReversement(props.row)">
            {{ formatPercent(getTauxReversement(props.row)) }}
          </span>
        </template>
        <template slot="totalLigneEch" slot-scope="props">
          <div class="has-text-right">
            {{ formatDevise(getTotalLigneEch(props.row)) }}
          </div>
        </template>
        <template slot="pourcentageTotal" slot-scope="props">
          <div class="has-text-right">
            {{ formatDevise(getPourcentageTotal(props.row)) }}
          </div>
        </template>
      </v-server-table>
      <transition
        name="custom-classes-transition"
        enter-active-class="animate__animated animate__slideInRight"
        leave-active-class="animate__animated animate__slideOutRight"
        :duration="10">
        <div class="componentBrief" v-show="showFiltre === true">
          <filtre-ventes-marche
            ref="filtreListeVentesMarche"
            @toggleFiltre="setShowFiltre"
            @recherche="makeRechercheFilter()"
          />
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import cloneDeep from 'lodash/cloneDeep';
import moment from 'moment';
import exportExcel from '@/api/exportExcel';
import { SET_LOCK_MENU } from '@/store/modules/societe-mutation-type';
import { SET_CRITERE_RECHERCHE_VENTES_MARCHE } from '@/store/modules/achat/marche-mutation-type';
import inProgress from '@/services/general/inProgress';
import calculNombreCritere from '@/services/general/calculNombreCritere';
import formatDate from '@/services/general/formatDate';
import formatDateIso from '@/services/general/formatIsoDate';
import formatPercent from '@/services/general/filters/formatPercent';
import messageErreur from '@/services/general/messageErreur';
import { formatDevise } from '@/services/general/filters/formatDevise';
import generalTransformer from '@/services/general/filters/serverTableDataTransformer';
import { getEcheaciersLigneByDate } from '@/services/achat/marche/planningVentesMarche';
import { genererCritereRechercheListeVente } from '@/services/achat/marche/genererCritereRecherche';
import getHeaderByMonthPlanningVentesSupport from '@/services/achat/marche/headerByMonthPlanningVentesSupport';
import RouterLinkBlank from '@/components/general/routerLinkBlank';
import FiltreVentesMarche from '@/components/achat/marche/ventesMarche/filterVentesMarche';
import PlanningVentesMarche from '@/components/achat/marche/ventesMarche/planningVentesMarche';
import exportExcelButton from '@/components/general/button/exportExcelButton';
import * as tableConfig from '@/configuration/clientTableConf';
import * as produitConf from '@/configuration/catalogue/produitConf';
import {
  CATALOGUE,
  COMMANDES,
  TIERS,
} from '@/configuration/permissionsBodacConf';
import { SENS_VENTE, TYPE_DEVIS, TYPE_COMMANDE } from '@/configuration/devisConf';
import { EXPORT_EXCEL_VENTE_SUPPORT } from '@/configuration/presse/presseConfig';
import { PLAN_MEDIA } from '@/configuration/presse/permissionsPresseConf';
import { MARCHE } from '@/configuration/achat/permissionsConf';

const Permissions = createNamespacedHelpers('permissions');
const Marche = createNamespacedHelpers('marche');
const Societe = createNamespacedHelpers('societe');

export default {
  name: 'ListeVentesMarche',
  props: {
    marche: {
      type: Object,
      required: true,
    },
  },
  components: {
    RouterLinkBlank,
    FiltreVentesMarche,
    exportExcelButton,
  },
  data() {
    return {
      showFiltre: false,
      loadingComponent: null,
      perms: {
        marche: MARCHE,
        planMedia: PLAN_MEDIA,
        catalogue: CATALOGUE,
        commande: COMMANDES,
        tiers: TIERS,
      },
    };
  },
  computed: {
    ...Permissions.mapGetters({
      checkPermissions: 'checkPermissions',
    }),
    ...Marche.mapGetters({
      items: 'ventesMarche',
      totalItems: 'totalVentesMarche',
      criteresRecherche: 'criteresRechercheVentesMarche',
      lastPayload: 'lastPayloadVentesMarche',
    }),
    ...Societe.mapGetters({
      lockMenu: 'lockMenu',
      societeActiveId: 'societeActiveId',
    }),
    nombreCritere() {
      const banCritereKey = ['auto', 'datesEcheance'];
      let nbCritere = calculNombreCritere(this.criteresRecherche, banCritereKey);
      if (this.criteresRecherche.datesEcheance) {
        nbCritere += this.criteresRecherche.datesEcheance.dateDebut ? 1 : 0;
      }
      return nbCritere <= 9 ? nbCritere : '+9';
    },
    columns() {
      const columns = [
        'planMedia', 'commande', 'produit', 'payeur', 'annonceur',
        'dateDevisCommande', 'dateTransmissionAuClient', 'dateTransmissionAuClub',
        'tauxReversement', 'totalLigneEch', 'pourcentageTotal', 'planning',
      ];
      return columns;
    },
    options() {
      return {
        sortIcon: tableConfig.SORT_ICON,
        headings: {
          planMedia: this.$t('detailMarche.ventesMarche.tabs.planMedia'),
          commande: this.$t('detailMarche.ventesMarche.tabs.commande'),
          produit: this.$t('detailMarche.ventesMarche.tabs.produit'),
          payeur: this.$t('detailMarche.ventesMarche.tabs.payeur'),
          annonceur: this.$t('detailMarche.ventesMarche.tabs.annonceur'),
          dateDevisCommande: this.$t('detailMarche.ventesMarche.tabs.dateDevisCommande'),
          dateTransmissionAuClient: this.$t('detailMarche.ventesMarche.tabs.dateTransmissionAuClient'),
          dateTransmissionAuClub: this.$t('detailMarche.ventesMarche.tabs.dateTransmissionAuClub'),
          tauxReversement: this.$t('detailMarche.ventesMarche.tabs.tauxReversement'),
          totalLigneEch: this.$t('detailMarche.ventesMarche.tabs.totalLigneEch'),
          pourcentageTotal: this.$t('detailMarche.ventesMarche.tabs.pourcentageTotal'),
          planning: (h) => {
            return this.getPlanningHeader(h);
          },
        },
        filterable: false,
        perPageValues: tableConfig.NO_PER_PAGE_VALUE,
        perPage: tableConfig.NO_PAGINATION,
        texts: tableConfig.TEXTS,
        pagination: {
          show: false,
        },
        templates: {
          planning: PlanningVentesMarche,
        },
        sortable: ['planMedia', 'commande', 'produit', 'dateDevisCommande', 'dateTransmissionAuClient', 'dateTransmissionAuClub'],
        columnsClasses: {
          ...this.columns.reduce((acc, val) => {
            acc[val] = 'is-size-9 sticky';
            return acc;
          }, {}),
          produit: 'planningTailleColonneId is-size-9 sticky ellipsis',
          payeur: 'planningTailleColonneId is-size-9 sticky ellipsis',
          annonceur: 'planningTailleColonneId is-size-9 sticky ellipsis',
          tauxReversement: 'colonneSelected is-size-9 sticky',
          planning: 'planningTailleColonnePlanning is-size-9 sticky',
        },
        requestFunction: ((data) => {
          if (!data.query) data.query = cloneDeep(this.criteresRecherche);
          data.query.societeId = this.societeActiveId;
          data.query.sens = SENS_VENTE;
          if (data.query.datesEcheance.dateDebut) {
            data.query.datesEcheance.dateDebut = formatDateIso(
              data.query.datesEcheance.dateDebut,
            );
          }
          if (data.query.datesEcheance.dateFin) {
            data.query.datesEcheance.dateFin = formatDateIso(
              data.query.datesEcheance.dateFin,
            );
          }
          if (data.query.planMediaIds.length > 0) {
            data.query.planMediaIds = data.query.planMediaIds.map(p => p.id);
          }
          if (data.query.produitsIds.length > 0) {
            data.query.produitsIds = data.query.produitsIds.map(p => p.id);
          } else {
            data.query.produitsIds = this.marche.produits.map((p) => p.id);
          }
          if (data.query.payeurs.length > 0) {
            data.query.payeurs = data.query.payeurs.map(p => p.id);
          }
          if (data.query.annonceurs.length > 0) {
            data.query.annonceurs = data.query.annonceurs.map(p => p.id);
          }
          if (data.query.afficherDevis) {
            data.query.typesDevisCommandeFacture.push(TYPE_DEVIS);
          }
          return this.actionsLoad(data);
        }),
        responseAdapter: (() => {
          return {
            data: this.items,
            count: this.totalItems,
          };
        }),
      };
    },
    totalColumnsTotalLigneEch() {
      const total = this.items
        .reduce((acc, val) => acc + this.getTotalLigneEch(val), 0);
      return total;
    },
    totalColumnsTotalPourcentage() {
      const total = this.items
        .reduce((acc, val) => acc + this.getPourcentageTotal(val), 0);
      return total;
    },
    months() {
      const { dateDebut, dateFin } = this.criteresRecherche.datesEcheance;
      const startMonth = moment(dateDebut).startOf('month');
      const datesEcheance = [{ date: moment(startMonth) }];
      startMonth.add(1, 'months');
      while (startMonth.isBefore(dateFin)) {
        datesEcheance.push({ date: moment(startMonth) });
        startMonth.add(1, 'months');
      }
      const finalDates = datesEcheance.map(el => ({
        ref: `total_${formatDateIso(el.date)}`,
        date: el.date,
      }));
      return finalDates;
    },
    exportExcelQuery() {
      return {
        typeDonnee: EXPORT_EXCEL_VENTE_SUPPORT,
        typeExport: null,
        filtres: generalTransformer(this.lastPayload),
      };
    },
  },
  methods: {
    ...Permissions.mapActions({
      showPopUpPermission: 'showPopup',
    }),
    ...Marche.mapActions({
      actionsLoad: 'loadListeVente',
    }),
    ...Marche.mapMutations({
      setCritereRecherche: SET_CRITERE_RECHERCHE_VENTES_MARCHE,
    }),
    ...Societe.mapMutations({
      setLockMenu: SET_LOCK_MENU,
    }),
    formatDevise(value) {
      return formatDevise(value);
    },
    formatPercent(value) {
      return formatPercent(value);
    },
    formatDate(date) {
      return formatDate(date);
    },
    getPlanningHeader(h) {
      return getHeaderByMonthPlanningVentesSupport(
        h,
        this.criteresRecherche.datesEcheance.dateDebut,
        this.criteresRecherche.datesEcheance.dateFin,
      );
    },
    inProgress() {
      inProgress(this.$i18n);
    },
    debutLoading() {
      if (this.loadingComponent) {
        this.loadingComponent.close();
      }
      this.loadingComponent = this.$buefy.loading.open();
    },
    finLoading() {
      this.loadingComponent.close();
    },
    recherche() {
      this.$refs.tableListeListeVentesMarche.refresh();
    },
    async setShowFiltre() {
      this.showFiltre = !this.showFiltre;
      if (this.showFiltre) await this.$refs.filtreListeVentesMarche.initializeFiltre();
      this.setLockMenu(!this.lockMenu);
    },
    makeRechercheFilter() {
      if (this.showFiltre) this.setShowFiltre();
      this.recherche();
    },
    async reinitialiser() {
      await this.$refs.filtreListeVentesMarche.initializeFiltre();
      this.$refs.filtreListeVentesMarche.viderCritere();
      this.$refs.filtreListeVentesMarche.appliquerCritere();
      this.showFiltre = false;
    },
    getPlanMedia(ligne) {
      if (!ligne?.devisCommande?.planMediaId) return null;
      return {
        code: ligne.devisCommande.planMedia.code,
        id: ligne.devisCommande.planMediaId,
      };
    },
    getRouteDevisCommande(ligne) {
      if (!ligne?.devisCommande) return null;
      const detail = {
        message: ligne.devisCommande.code,
        to: { name: 'c.bodac.devis', params: { id: ligne.devisCommande.id } },
      };
      if (ligne.devisCommande.type === TYPE_COMMANDE) {
        detail.to.name = 'c.bodac.commandes';
        if (ligne.devisCommande.interne) detail.to.name = 'c.bodac.commandes-venteInterne';
      }
      return detail;
    },
    getProduit(ligne) {
      if (!ligne?.produitId) return null;
      const produitConfig = produitConf.CATEGORIE_PRODUIT
        .find(p => p.code === ligne.typeProduit);
      return {
        reference: ligne.produit.reference,
        id: ligne.produitId,
        routeDetail: produitConfig?.routeDetail,
      };
    },
    getPayeur(ligne) {
      if (!ligne?.devisCommande?.intervenants) return null;
      return ligne.devisCommande.intervenants
        .find(i => i.payeur);
    },
    getAnnonceur(ligne) {
      if (!ligne?.devisCommande?.intervenants) return null;
      return ligne.devisCommande.intervenants
        .find(i => i.annonceur);
    },
    getTauxReversement(ligne) {
      if (!ligne?.produit) return null;
      const { dateDebut, dateFin } = this.criteresRecherche.datesEcheance;
      const regle = ligne.produit.reglesAchat
        .find(r => moment(r.dateFin).isAfter(dateDebut)
          && moment(r.dateDebut).isBefore(dateFin));
      if (!regle) return null;
      return regle?.pourcentageReversement;
    },
    getTotalLigneEch(ligne) {
      return ligne.echeanciersLigne
        .reduce((acc, val) => acc + val.montant, 0);
    },
    getPourcentageTotal(ligne) {
      const taux = this.getTauxReversement(ligne) || 0;
      const total = this.getTotalLigneEch(ligne);
      return (taux * total) / 100;
    },
    getTotalEcheancierByMonth(month) {
      const echs = getEcheaciersLigneByDate(
        this.items.map(ech => ech.echeanciersLigne).flat(),
        month.date,
      );
      const total = echs.reduce((acc, val) => acc + val.montantAPrendre, 0);
      return total;
    },
    async exportExcel() {
      const loadingComponent = this.$buefy.loading.open();
      try {
        const response = await exportExcel({
          typeDonnee: EXPORT_EXCEL_VENTE_SUPPORT,
          typeExport: null,
          filtres: generalTransformer(this.lastPayload),
        });
        const link = this.$refs.lienDownload;
        link.href = window.URL.createObjectURL(response.data);
        const disposition = response.request.getResponseHeader('Content-Disposition');
        link.download = decodeURI(disposition.match(/filename\*=UTF-8''(.*?)$/)[1]);
        link.click();
        loadingComponent.close();
      } catch (e) {
        loadingComponent.close();
        messageErreur(this.$i18n);
        throw e;
      }
    },
  },
  beforeDestroy() {
    if (this.lockMenu) this.setLockMenu(false);
    this.setCritereRecherche(genererCritereRechercheListeVente());
  },
};
</script>
